import React from 'react';

import * as ManifestUI from '@project44/manifest-ds';

import { UIDomainEnums } from '../../../UILibrary';
import { HTMLFormElementsFactory } from '../../../UILibrary/infrastructure/factories/HTMLFormElements.factory';
import {
  IFormClient,
  IHTMLFormProperty,
  THTMLFormPropertyModeler,
} from '../../domain/interface';

export abstract class HTMLFormProperty implements IHTMLFormProperty {
  public abstract isEditable: boolean;
  public abstract isDisplayable: boolean;
  public abstract initialValue: string;
  public abstract inputType: UIDomainEnums.HTMLFormTypeEnum;
  public abstract renderFormTypeComponent: React.FC<{
    propertyName: string;
    formClient: IFormClient;
    isLoading?: boolean;
  }>;
  public helperText: string | undefined = undefined;
  public label: string | undefined = undefined;
  public name: string | undefined = undefined;

  public renderFormControlComponent: React.FC<{
    propertyName: string;
    label?: string;
    formClient: IFormClient;
    isLoading?: boolean;
  }> = ({ propertyName, label, formClient }) => {
    return (
      <HTMLFormElementsFactory.composeFormControl
        data-test-id={propertyName}
        validationState={this.setValidationStateProp(propertyName, formClient)}
        helperText={this.helperText}
        label={label || this.label}
        labelProps={{
          htmlFor: propertyName,
        }}
      />
    );
  };

  protected setValidationStateProp = (
    ...formikAdapterConfig: THTMLFormPropertyModeler
  ): ManifestUI.TextFieldProps['validationState'] => {
    const [formPropertyKey, formClient] = formikAdapterConfig;

    return !!formClient.touched[formPropertyKey] &&
      !!formClient.errors[formPropertyKey]
      ? 'invalid'
      : 'valid';
  };

  protected static readonly configureHTMLFormProperty = (
    payload?: Partial<IHTMLFormProperty>,
  ): Omit<
    IHTMLFormProperty,
    'renderFormTypeComponent' | 'renderFormControlComponent'
  > => {
    const {
      name = undefined,
      inputType = UIDomainEnums.HTMLFormTypeEnum.UNKNOWN,
      isEditable = false,
      helperText = undefined,
      isDisplayable = false,
      initialValue = '',
      label = '',
    } = payload || {};

    return {
      helperText,
      label,
      name,
      isEditable,
      isDisplayable,
      initialValue,
      inputType,
    };
  };
}
